import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
	fetchOnboardingMe,
	sendUserOTP,
	login,
	signup,
	verifyOTP,
	fetchSsoSession,
	logoutUser,
} from '../services/auth';
import { useNavigate } from 'react-router-dom';
import { encodeEmailToToken } from '../utils/controller';
import { setAuthTokenToLocalStorage } from '../utils';
import { useDispatch } from 'react-redux';
import { setAuthToken, setIsOnboardingCompleted, clearUserInfo } from '../store/action/userInfo';
import { clearNotification } from '../store/action/notifications';

/**
 * Auth actions, with STABLE function identities.
 *
 * Why the memoisation matters: these used to be plain functions rebuilt on
 * every render, so `useAuth()` handed back four brand-new references each
 * time. Calling them from an event handler is unaffected — but putting one in
 * a `useEffect` dependency array made that effect re-run on EVERY render. When
 * such an effect also dispatched to Redux, the dispatch re-rendered the
 * component, which produced new identities, which re-ran the effect: an
 * infinite loop that hammered the API. That is exactly what happened in
 * router/PrivateRoutes.js with `/user/info`.
 *
 * Both dependencies below are genuinely stable, which is what makes this
 * worthwhile rather than cosmetic:
 *   - `dispatch` is stable per store (react-redux).
 *   - `navigate` is stable because this app uses createBrowserRouter (see
 *     router/index.js), so react-router resolves useNavigate to its
 *     data-router implementation, memoised on [router, routeId].
 *
 * Everything else these close over — toast, the service functions, the action
 * creators — is a module import and is not reactive, so it is correctly absent
 * from the dependency arrays.
 */
const useAuth = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const userSignup = useCallback(
		async (params) => {
			try {
				const data = await signup(params);
				if (!data.success) {
					toast.error(data.message || 'Something went wrong!');
					return data;
				} else {
					if (data.token) {
						setAuthTokenToLocalStorage(data.token);
						dispatch(setAuthToken({ authToken: data.token }));
					}
					return data;
				}
			} catch (error) {
				toast.error('Something went wrong! while signing up.');
			}
		},
		[dispatch],
	);

	const userOTPVerification = useCallback(
		async (params) => {
			try {
				const data = await verifyOTP(params);
				if (!data.success) {
					toast.error(data.message || 'Something went wrong! while verifying the OTP.');
					return;
				}
				if (data.token) {
					setAuthTokenToLocalStorage(data.token);
					dispatch(setAuthToken({ authToken: data.token }));
				}
				toast.success('OTP verified successfully');

				return data;
			} catch (error) {
				toast.error('Something went wrong! while verifying the OTP.');
				throw error;
			}
		},
		[dispatch],
	);

	const userLogin = useCallback(
		async (params) => {
			try {
				const { email, password, subdomain } = params;
				const data = await login({ email, password, subdomain });
				if (!data.success) {
					toast.error(data?.message || 'Something went wrong!');
					return data;
				} else {
					// TODO store the auth token and onboarding status with userinfo
					setAuthTokenToLocalStorage(data.token);
					dispatch(setAuthToken({ authToken: data.token, isAuthenticated: true }));
					window.dispatchEvent(new Event('auth-change'));
					return data;
				}
			} catch (error) {
				toast.error('Something went wrong! while login.');
				throw error;
			}
		},
		[dispatch],
	);

	const userOnboardingMe = useCallback(
		async (params) => {
			try {
				const data = await fetchOnboardingMe();
				if (!data.success) {
					toast.error('Something went wrong! while fetching the onboarding status.');
					return;
				}

				const onboarding = data.data;
				const status = onboarding.onboarding_status;

				switch (status) {
					case 'FORGOT_PASSWORD':
						navigate('/createNewPassword');
						break;

					case 'REGISTERED':
					case 'OTP_PENDING': {
						if (!params?.email) {
							toast.error('Email is missing. Please login again.');
							return;
						}
						const otpRes = await sendUserOTP(params.email);
						if (!otpRes.success) {
							toast.error(otpRes.message || 'Failed to send OTP');
							return;
						}
						toast.success('Please verify your email.');
						const token = encodeEmailToToken(params.email);
						navigate(`/otpVerify/?token=${token}&page_login=true`);
						break;
					}

					case 'VERIFIED':
					case 'SUBSCRIPTION_PENDING':
						navigate('/subscription-plan');
						break;

					case 'SUBSCRIPTION_COMPLETED':
						navigate('/payment-success');
						break;

					case 'ORG_DRAFT':
						navigate('/organizationDetail');
						break;

					case 'COMPLETED': {
						const sub_domain = onboarding.sub_domain;
						dispatch(setIsOnboardingCompleted({ isOnboardingCompleted: true }));
						localStorage.setItem('suburl', sub_domain);
						if (params.redirectParam) {
							navigate(`/user/${sub_domain}/${params.redirectParam}`);
						} else {
							navigate(`/user/${sub_domain}/home`);
						}
						break;
					}

					default:
						navigate('/');
				}
			} catch (error) {}
		},
		[dispatch, navigate],
	);

	const checkSsoSession = useCallback(
		async (subdomain) => {
			try {
				const data = await fetchSsoSession(subdomain);
				if (data?.success && data?.authenticated && data?.token) {
					setAuthTokenToLocalStorage(data.token);
					if (data.refreshToken) {
						localStorage.setItem('refreshToken', data.refreshToken);
					}
					const sub_domain = data.data?.organisation?.sub_domain;
					if (sub_domain) {
						localStorage.setItem('suburl', sub_domain);
					}
					dispatch(setAuthToken({ authToken: data.token, isAuthenticated: true }));
					dispatch(setIsOnboardingCompleted({ isOnboardingCompleted: true }));
					window.dispatchEvent(new Event('auth-change'));
					return data;
				}
				return data || { success: false, authenticated: null, error: true };
			} catch (error) {
				return { success: false, authenticated: null, error: true };
			}
		},
		[dispatch],
	);

	const userLogout = useCallback(async () => {
		try {
			await logoutUser();
		} catch (err) {
			console.error('Logout error:', err);
		}
		sessionStorage.setItem('just_logged_out', 'true');
		localStorage.clear();
		dispatch(clearNotification());
		dispatch(setAuthToken({ authToken: null }));
		dispatch(clearUserInfo());
		dispatch(setIsOnboardingCompleted({ isOnboardingCompleted: false }));
	}, [dispatch]);

	// The returned OBJECT is memoised too, not just the functions on it.
	// Without this, `const auth = useAuth()` would still hand back a new object
	// every render, so putting `auth` itself in a dependency array would
	// reintroduce exactly the instability the useCallbacks above remove.
	return useMemo(
		() => ({
			userOnboardingMe,
			userLogin,
			userSignup,
			userOTPVerification,
			checkSsoSession,
			userLogout,
		}),
		[
			userOnboardingMe,
			userLogin,
			userSignup,
			userOTPVerification,
			checkSsoSession,
			userLogout,
		],
	);
};

export default useAuth;
